import { trackProductEvent, type ProductBaseProperties } from "./product";

export const SOCIAL_FLOW = "social";

export type SocialEventName =
  | "social_opened"
  | "social_return_within_24h"
  | "social_preview_viewed"
  | "social_preview_cta_clicked"
  | "experience_started"
  | "experience_created"
  | "experience_updated"
  | "experience_deleted"
  | "experience_viewed"
  | "experience_reaction_added"
  | "also_experienced_clicked"
  | "experience_bookmarked"
  | "experience_unbookmarked"
  | "comments_opened"
  | "comment_created"
  | "comment_updated"
  | "comment_deleted"
  | "comment_reply_started"
  | "comment_liked"
  | "comment_unliked"
  | "similar_people_viewed"
  | "profile_viewed"
  | "social_profile_updated"
  | "profile_banner_selected"
  | "question_viewed"
  | "question_started"
  | "question_created"
  | "question_answered"
  | "question_bookmarked"
  | "question_unbookmarked"
  | "post_gira_prompt_shown"
  | "post_gira_prompt_clicked"
  | "post_gira_experience_created"
  | "share_started"
  | "whatsapp_share_clicked"
  | "connection_requested"
  | "connection_created"
  | "report_submitted"
  | "user_blocked"
  | "privacy_changed"
  | "messages_opened"
  | "conversation_opened"
  | "message_sent"
  | "conversation_read"
  | "social_onboarding_started"
  | "social_onboarding_field_answered"
  | "social_onboarding_review_shown"
  | "social_onboarding_corrected"
  | "social_onboarding_completion_succeeded"
  | "social_onboarding_completion_failed"
  | "social_onboarding_resumed"
  | "social_verification_progress_viewed"
  | "social_verification_earned"
  | "social_verification_extended"
  | "social_verification_cta_clicked"
  | "social_verification_celebration_continue"
  | "social_verification_celebration_profile"
  | "social_create_fab_opened"
  | "social_create_option_clicked";

/** localStorage key for last social push click (return-within-24h funnel). */
export const SOCIAL_PUSH_CLICK_AT_KEY = "social.pushClickAt";

const SOCIAL_PUSH_NOTIFICATION_TYPES = new Set([
  "CONNECTION_CREATED",
  "SOCIAL_MESSAGE",
  "EXPERIENCE_REACTION",
  "EXPERIENCE_SIMILAR",
  "EXPERIENCE_COMMENT",
  "EXPERIENCE_COMMENT_LIKE",
  "QUESTION_ANSWERED",
  "POST_GIRA_PROMPT",
  "QUESTION_SUGGESTION",
  "MODERATION_ACTION",
  "SOCIAL_VERIFICATION_EARNED",
  "SOCIAL_VERIFICATION_EXPIRING",
  "SOCIAL_VERIFICATION_EXPIRED",
  "SOCIAL_CONTENT_DIGEST",
]);

export const isSocialPushNotificationType = (
  type: string | null | undefined,
): boolean => Boolean(type && SOCIAL_PUSH_NOTIFICATION_TYPES.has(type));

export const recordSocialPushClick = (
  notificationType: string | null | undefined,
): void => {
  if (!isSocialPushNotificationType(notificationType)) return;
  try {
    window.localStorage.setItem(SOCIAL_PUSH_CLICK_AT_KEY, String(Date.now()));
  } catch {
    // ignore storage failures
  }
};

/**
 * If the user opened Social within 24h of a social push click, fire
 * `social_return_within_24h` once and clear the stored click.
 */
export const maybeTrackSocialReturnWithin24h = (
  baseProperties: ProductBaseProperties,
): void => {
  try {
    const raw = window.localStorage.getItem(SOCIAL_PUSH_CLICK_AT_KEY);
    if (!raw) return;
    const clickedAt = Number(raw);
    if (!Number.isFinite(clickedAt)) {
      window.localStorage.removeItem(SOCIAL_PUSH_CLICK_AT_KEY);
      return;
    }
    const ageMs = Date.now() - clickedAt;
    if (ageMs < 0 || ageMs > 24 * 60 * 60 * 1000) {
      window.localStorage.removeItem(SOCIAL_PUSH_CLICK_AT_KEY);
      return;
    }
    trackSocialEvent("social_return_within_24h", baseProperties, {
      hours_since_push_click: Math.round((ageMs / (60 * 60 * 1000)) * 10) / 10,
    });
    window.localStorage.removeItem(SOCIAL_PUSH_CLICK_AT_KEY);
  } catch {
    // ignore storage failures
  }
};

export const trackSocialEvent = (
  event: SocialEventName,
  baseProperties: ProductBaseProperties,
  eventProperties?: Record<string, unknown>
): void => {
  trackProductEvent(event, baseProperties, eventProperties, SOCIAL_FLOW);
};
